import { useState } from "react";
import MainLayout from "@/components/feature/MainLayout";
import { safetyInspections } from "@/mocks/systemModules";
import SafetyAICards from "./components/SafetyAICards";
import SafetyAIRisk from "./components/SafetyAIRisk";

const statusConfig: Record<string, { color: string; bg: string; label: string }> = {
  "待安检": { color: "text-amber-600", bg: "bg-amber-50", label: "待安检" },
  "已完成": { color: "text-green-600", bg: "bg-green-50", label: "已完成" },
  "整改中": { color: "text-orange-600", bg: "bg-orange-50", label: "整改中" },
  "逾期": { color: "text-red-600", bg: "bg-red-50", label: "逾期" },
};

export default function SafetyPage() {
  const [filter, setFilter] = useState("全部");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<typeof safetyInspections[0] | null>(null);
  const [scheduleModal, setScheduleModal] = useState(false);

  const statuses = ["全部", "待安检", "已完成", "整改中", "逾期"];
  const filtered = safetyInspections.filter(s => {
    const matchStatus = filter === "全部" || s.status === filter;
    const matchSearch = !search || s.userName.includes(search) || s.address.includes(search) || s.id.includes(search);
    return matchStatus && matchSearch;
  });

  const stats = {
    total: safetyInspections.length,
    pending: safetyInspections.filter(s => s.status === "待安检").length,
    done: safetyInspections.filter(s => s.status === "已完成").length,
    rectify: safetyInspections.filter(s => s.status === "整改中").length,
    overdue: safetyInspections.filter(s => s.status === "逾期").length,
  };

  return (
    <MainLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">安检管理</h1>
            <p className="text-sm text-gray-500 mt-0.5">燃气用户安全检查计划与记录管理</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setScheduleModal(true)}
              className="flex items-center gap-2 bg-[#E8531A] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#d44a15] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-check-line"></i>
              批量排期
            </button>
            <button className="flex items-center gap-2 border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-download-line"></i>
              导出
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-3">
          {[
            { label: "安检总数", value: stats.total, color: "text-gray-800", bg: "bg-gray-50" },
            { label: "待安检", value: stats.pending, color: "text-amber-600", bg: "bg-amber-50" },
            { label: "已完成", value: stats.done, color: "text-green-600", bg: "bg-green-50" },
            { label: "整改中", value: stats.rectify, color: "text-orange-600", bg: "bg-orange-50" },
            { label: "逾期未检", value: stats.overdue, color: "text-red-600", bg: "bg-red-50" },
          ].map((s, i) => (
            <div key={i} className={`${s.bg} rounded-xl p-4`}>
              <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-xs text-gray-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {/* AI Cards */}
        <SafetyAICards />

        {/* AI Risk Assessment */}
        <SafetyAIRisk />

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {statuses.map(s => (
                <button
                  key={s}
                  onClick={() => setFilter(s)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${filter === s ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="flex-1 relative min-w-48">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="text"
                placeholder="搜索用户名/地址/编号..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#E8531A]"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                {["安检编号", "用户姓名", "安检地址", "安检类型", "计划日期", "安检员", "状态", "评分", "操作"].map(h => (
                  <th key={h} className="text-left text-xs font-medium text-gray-500 px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(item => {
                const cfg = statusConfig[item.status];
                return (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-xs font-mono text-gray-500">{item.id}</td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-800">{item.userName}</div>
                      <div className="text-xs text-gray-400">{item.phone}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 max-w-40 truncate">{item.address}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{item.type}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{item.scheduledDate}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{item.inspector || <span className="text-gray-300">未分配</span>}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      {item.score !== null ? (
                        <span className={`text-sm font-bold ${item.score >= 90 ? "text-green-600" : item.score >= 70 ? "text-amber-600" : "text-red-600"}`}>
                          {item.score}分
                        </span>
                      ) : <span className="text-gray-300 text-xs">-</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSelected(item)}
                          className="text-xs text-[#E8531A] hover:underline cursor-pointer whitespace-nowrap"
                        >
                          详情
                        </button>
                        {item.status === "待安检" && (
                          <button className="text-xs text-gray-500 hover:underline cursor-pointer whitespace-nowrap">
                            录入结果
                          </button>
                        )}
                        {item.status === "逾期" && (
                          <button className="text-xs text-red-500 hover:underline cursor-pointer whitespace-nowrap">
                            催检
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl p-6 w-[480px] max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-900">安检详情 - {selected.id}</h3>
              <button onClick={() => setSelected(null)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-400">用户姓名</div>
                  <div className="text-sm font-medium text-gray-800 mt-0.5">{selected.userName}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-400">联系电话</div>
                  <div className="text-sm font-medium text-gray-800 mt-0.5">{selected.phone}</div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-400">安检地址</div>
                <div className="text-sm font-medium text-gray-800 mt-0.5">{selected.address}</div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-400">安检类型</div>
                  <div className="text-sm font-medium text-gray-800 mt-0.5">{selected.type}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-400">计划日期</div>
                  <div className="text-sm font-medium text-gray-800 mt-0.5">{selected.scheduledDate}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-400">安检员</div>
                  <div className="text-sm font-medium text-gray-800 mt-0.5">{selected.inspector || "未分配"}</div>
                </div>
              </div>
              {selected.score !== null && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-400 mb-2">安检评分</div>
                  <div className="flex items-center gap-3">
                    <div className={`text-3xl font-bold ${selected.score >= 90 ? "text-green-600" : selected.score >= 70 ? "text-amber-600" : "text-red-600"}`}>
                      {selected.score}
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${selected.score >= 90 ? "bg-green-500" : selected.score >= 70 ? "bg-amber-500" : "bg-red-500"}`}
                        style={{ width: `${selected.score}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}
              {selected.issues.length > 0 && (
                <div className="bg-red-50 rounded-lg p-3">
                  <div className="text-xs text-red-500 font-medium mb-2">发现问题</div>
                  {selected.issues.map((issue, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-red-700">
                      <i className="ri-error-warning-line text-red-400"></i>
                      {issue}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {scheduleModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setScheduleModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-[400px]" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-gray-900 mb-4">批量安检排期</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">排期范围</label>
                <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]">
                  <option>全部待安检用户</option>
                  <option>逾期未检用户</option>
                  <option>指定区域用户</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">开始日期</label>
                <input type="date" defaultValue="2026-04-15" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">每日安检数量</label>
                <input type="number" defaultValue={20} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]" />
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={() => setScheduleModal(false)} className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">取消</button>
                <button onClick={() => setScheduleModal(false)} className="flex-1 bg-[#E8531A] text-white py-2 rounded-lg text-sm cursor-pointer hover:bg-[#d44a15] whitespace-nowrap">确认排期</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
